import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  CloseButton,
  Flex,
  FormControl,
  Heading,
} from "@chakra-ui/react";
import { useColorModeValue } from "@chakra-ui/system";
import { Field, Formik } from "formik";
import DefaultAuth from "layouts/auth/Default";
import { axiosService } from "network/axios";
import { useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setAlertMessageRequest } from "store/ui/reducer";
import { AuthInput } from "components/auth/AuthInput";
import { useAuthStyles } from "theme/hooks/useAuthStyles";

type ForgotFormInputs = {
  email: string;
};

export default function Forgot() {
  const initialValues: ForgotFormInputs = {
    email: "",
  };
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  // Use customer auth styles
  const authStyles = useAuthStyles('customer');
  const textColor = useColorModeValue("navy.700", "white");
  const [isProcessing, setIsProcessing] = useState(false);
  const [statusCode, setStatusCode] = useState(null);

  const onRequestResetPassword = async (payload: any) => {
    setIsProcessing(true);
    setStatusCode(null);
    try {
      await axiosService.post("/recovery", {
        ...payload,
      });
      setStatusCode("succeed");
    } catch (error: any) {
      setStatusCode(null);
      dispatch(
        setAlertMessageRequest({
          type: "error",
          message:
            error?.data?.messages?.error ||
            "Error occured in requesting password reset.",
        })
      );
      return;
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <DefaultAuth>
      <Flex
        {...authStyles.container}
        maxW="520px"
        w="100%"
        mx="auto"
        my="auto"
        h="fit-content"
        alignItems="start"
        justifyContent="center"
        flexDirection="column"
      >
        <Heading
          w="100%"
          color={textColor}
          {...authStyles.heading}
          mt={"30px"}
          textAlign="center"
        >
          Password Recovery
        </Heading>
        {statusCode !== null && (
          <Alert status="success" mb="20px">
            <AlertIcon color={"#222"} />
            <Box>
              <AlertDescription color={"#222"} fontSize="14px">
                Your request was submitted successfully. Please check your email
                box to reset the password.
              </AlertDescription>
            </Box>
            <CloseButton
              ml="auto"
              position="relative"
              color="#222"
              onClick={() => setStatusCode(null)}
            />
          </Alert>
        )}
        <Formik
          initialValues={initialValues}
          validateOnChange={false}
          validateOnBlur={false}
          onSubmit={(values: any) => {
            onRequestResetPassword(values);
          }}
        >
          {({ handleSubmit, errors, submitCount }) => (
            <form onSubmit={handleSubmit} style={{ width: "100%" }}>
              <Flex
                zIndex="2"
                direction="column"
                padding={authStyles.padding.desktop.form}
                w="full"
              >
                <FormControl isInvalid={submitCount > 0 && !!errors.email}>
                  <Field name="email" validate={(value: any) => {
                    let error;
                    if ((value || "").length < 1) {
                      error = "Email Address is required.";
                    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(value)) {
                      error = "Invalid email format.";
                    }
                    return error;
                  }}>
                    {({ field }: any) => (
                      <AuthInput
                        {...field}
                        role="customer"
                        chakraVariant="authCustomerNew"
                        type="email"
                        id="email"
                        placeholder="Email Address"
                        isInvalid={submitCount > 0 && !!errors.email}
                        onChange={(e: any) => {
                          field.onChange(e);
                        }}
                      />
                    )}
                  </Field>
                </FormControl>
              </Flex>
              <Flex
                flexDirection="column"
                justifyContent="center"
                alignItems="start"
                w="full"
                rowGap="16px"
                padding={authStyles.padding.desktop.footer}
              >
                <Button
                  type="submit"
                  fontSize="16px"
                  bgColor="#3A7BFF"
                  fontWeight="600"
                  h="48px"
                  color="#FFFFFF"
                  borderRadius="25px"
                  border="1px solid #2C63E5"
                  w="100%"
                  isLoading={isProcessing}
                  _hover={{
                    bgColor: "#FFFFFF",
                    color: "#2C63E5",
                  }}
                  _loading={{ pointerEvents: "none" }}
                  _active={{
                    bgColor: "#FFFFFF",
                    color: "#2C63E5",
                  }}
                >
                  Submit
                </Button>

                <Button
                  h="48px"
                  fontSize="14px"
                  bgColor="white"
                  color="#3A7BFF"
                  borderRadius="24px"
                  border="1px solid #3A7BFF"
                  fontWeight="600"
                  w="100%"
                  mb="16px"
                  _hover={{
                    bgColor: "#3A7BFF",
                    color: "#FFF",
                  }}
                  _active={{
                    bgColor: "#FFFFFF",
                    color: "#3A7BFF",
                  }}
                  onClick={() => {
                    navigate("/auth/sign-in");
                  }}
                >
                  Back
                </Button>
              </Flex>
            </form>
          )}
        </Formik>
      </Flex>
    </DefaultAuth>
  );
}
